import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import POSPinGate from "./POSPinGate";

/**
 * Route wrapper for /pos and /pos/bar.
 *
 * Two auth paths are accepted:
 *   1. Full admin/editor session (regular email/password login) — matched
 *      via ``AuthContext`` and rendered immediately.
 *   2. POS-scoped 12h session issued via ``/auth/pos/pin-login`` — stored
 *      in localStorage. We probe `/pos/catalog` (the lightest scoped
 *      endpoint) with the Bearer token to confirm the token is still
 *      valid.
 *
 * When neither works, we render the PIN gate — no redirect to /connexion,
 * so the tablet stays inside the POS surface for the whole shift.
 */
export default function POSAuthGate({ children }) {
  const { user, loading } = useAuth();
  const [posOk, setPosOk] = useState(null); // null=checking, true=ok, false=need pin
  const isStaff = user && (user.role === "admin" || user.role === "editor");

  useEffect(() => {
    if (loading || isStaff) { setPosOk(null); return; }
    // Probe the POS scope with whatever token localStorage has (if any).
    (async () => {
      if (!localStorage.getItem("access_token")) { setPosOk(false); return; }
      try {
        await api.get("/pos/catalog");
        setPosOk(true);
      } catch {
        setPosOk(false);
      }
    })();
  }, [loading, isStaff]);

  if (loading || (!isStaff && posOk === null)) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] text-muted-foreground" data-testid="pos-auth-loading">
        Chargement…
      </div>
    );
  }
  if (isStaff || posOk) return children;
  return <POSPinGate onAuth={() => setPosOk(true)} />;
}
